"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getMapContainerCorners = exports.getMarkerIconForState = exports.removeMarkers = exports.addMarkers = void 0;
const maplibre_gl_csp_js_1 = __importDefault(require("maplibre-gl/dist/maplibre-gl-csp.js"));
const markerStatesManager_1 = require("./markerStatesManager");
const constants_1 = require("./constants");
const makeAddress = (location) => {
    const { street1, street2, suite, city = '', state = '', zip = '' } = location;
    const streetAddress = [street1, street2, suite].filter(Boolean).join(', ');
    const stateAndZip = [state, zip].filter(Boolean).filter(Boolean).join(' ');
    return [streetAddress, city, stateAndZip].filter(Boolean).join(', ');
};
const validateSvgContent = (svgString) => {
    if (!svgString || typeof svgString !== 'string')
        return false;
    const maliciousPatterns = [
        /<script/i,
        /javascript:/i,
        /on\w+\s*=/i,
        /<iframe/i,
        /<object/i,
        /<embed/i,
        /<foreignObject/i,
        /xlink:href\s*=\s*["']javascript:/i
    ];
    const hasMaliciousContent = (content) => maliciousPatterns.some((pattern) => pattern.test(content));
    if (svgString.startsWith('data:image/svg+xml')) {
        try {
            const decoded = decodeURIComponent(svgString.split(',')[1] || '');
            return decoded.includes('<svg') && !hasMaliciousContent(decoded);
        }
        catch (_a) {
            return false;
        }
    }
    if (svgString.trim().startsWith('<svg') &&
        svgString.trim().endsWith('</svg>')) {
        return !hasMaliciousContent(svgString);
    }
    return false;
};
const createMarkerIcon = (svgString, location) => {
    const markerElem = createImageMarker(location);
    markerElem.src = svgString;
    return markerElem;
};
const createImageMarker = (location) => {
    const markerElem = document.createElement('img');
    markerElem.style.cursor = 'pointer';
    const address = makeAddress(location);
    markerElem.alt = `${location.name}. ${address}`;
    markerElem.setAttribute('tabIndex', '0');
    return markerElem;
};
const createCustomMarkerElem = (content, location) => {
    const markerElem = document.createElement('span');
    markerElem.classList.add('marker');
    const address = makeAddress(location);
    markerElem.setAttribute('aria-label', `${content}. ${location.name}. ${address}`);
    const markerContentElem = document.createElement('b');
    markerContentElem.appendChild(document.createTextNode(content));
    markerElem.appendChild(markerContentElem);
    return markerElem;
};
/**
 * Adds markers to the AWS Map instance with support for custom SVG icons.
 *
 * @param params - Configuration object for adding markers
 * @param params.locations - Array of location objects to create markers for
 * @param params.awsMapInstance - MapLibre GL JS map instance
 * @param params.isStaticMap - Whether the map is static (affects popover behavior)
 * @param params.defaultMarkerIcon - Optional global SVG content for all markers
 * @returns Array of marker references with metadata for external manipulation
 */
const addMarkers = ({ locations, awsMapInstance, isStaticMap, onMarkerClick, defaultMarkerIcon, enableAdvancedMarkers }) => {
    return locations
        .map((location, index) => {
        const { id, coordinates, popover, isSelected, isDisabled } = location;
        if (awsMapInstance && coordinates && id) {
            let markerElem;
            if (enableAdvancedMarkers) {
                markerElem = createImageMarker(location);
            }
            else if (defaultMarkerIcon && validateSvgContent(defaultMarkerIcon)) {
                markerElem = createMarkerIcon(defaultMarkerIcon, location);
            }
            else {
                markerElem = createCustomMarkerElem((index + 1).toString(), location);
            }
            const marker = new maplibre_gl_csp_js_1.default.Marker({
                element: markerElem,
                opacity: isDisabled ? ' 0.5' : '1.0'
            })
                .setLngLat(coordinates)
                .addTo(awsMapInstance);
            const meta = {
                location,
                isSelected: Boolean(isSelected),
                isDisabled: Boolean(isDisabled)
            };
            if (isSelected) {
                marker.addClassName('selected');
            }
            if (isDisabled) {
                // not used for styling currently, but is used by tests
                marker.addClassName('disabled');
            }
            if (!isStaticMap && popover) {
                const popup = new maplibre_gl_csp_js_1.default.Popup({ offset: 25 }).setHTML(popover);
                marker.setPopup(popup);
            }
            // Add click event handler if provided
            if (!isStaticMap) {
                if (onMarkerClick) {
                    markerElem.addEventListener('click', (event) => {
                        event.stopPropagation();
                        onMarkerClick({
                            marker,
                            meta
                        });
                    });
                    // Add keyboard support
                    markerElem.addEventListener('keyup', (event) => {
                        const keyboardEvent = event;
                        if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
                            event.preventDefault();
                            event.stopPropagation();
                            onMarkerClick({
                                marker,
                                meta
                            });
                        }
                    });
                }
                // Make marker element clickable
                markerElem.style.cursor = 'pointer';
                markerElem.setAttribute('role', 'button');
                markerElem.setAttribute('tabindex', '0');
                markerElem.setAttribute('aria-label', `${location.name}. ${makeAddress(location)}`);
                if (enableAdvancedMarkers) {
                    new markerStatesManager_1.MarkerIconManager().attachListeners(markerElem);
                }
            }
            return { marker, meta };
        }
        return null;
    })
        .filter(Boolean);
};
exports.addMarkers = addMarkers;
const removeMarkers = (markers) => {
    markers.forEach(({ marker }) => marker.remove());
};
exports.removeMarkers = removeMarkers;
const getMarkerIconForState = (state, dimensions = {
    w: 48,
    h: 48
}) => {
    const markerStateValue = constants_1.MarkerState[state];
    const svgString = constants_1.ICONS[markerStateValue](dimensions);
    const encodedSvg = encodeURIComponent(svgString)
        .replace(/'/g, '%27')
        .replace(/"/g, '%22');
    return `data:image/svg+xml,${encodedSvg}`;
};
exports.getMarkerIconForState = getMarkerIconForState;
const getMapContainerCorners = (mapRef) => {
    const bounds = mapRef.getBounds();
    if (bounds) {
        const nw = bounds.getNorthWest();
        const se = bounds.getSouthEast();
        return {
            topLeft: { lat: nw.lat, lng: nw.lng },
            bottomRight: { lat: se.lat, lng: se.lng }
        };
    }
    return {};
};
exports.getMapContainerCorners = getMapContainerCorners;
//# sourceMappingURL=utils.js.map